// Shared NDA document component — used by NDAPage (user view) and AdminNDAViewPage (admin view).

export const NDA_PRINT_STYLES = `
  @page { size: A4; margin: 2.2cm 2cm; }
  @media print {
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    body { background: #fff !important; }
    .print-hide { display: none !important; }
    .nda-scroll-container {
      max-height: none !important; overflow: visible !important;
      border: none !important; box-shadow: none !important;
      border-radius: 0 !important; padding: 0 !important;
    }
    .nda-document { font-size: 10.5pt; line-height: 1.65; color: #000 !important; }
    .nda-document h2 { font-size: 14pt; margin-bottom: 4pt; }
    .nda-document h3 { font-size: 10.5pt; margin-top: 14pt; margin-bottom: 5pt; page-break-after: avoid; }
    .nda-document section { margin-bottom: 10pt; page-break-inside: avoid; }
    .nda-document ol { padding-left: 18pt; }
    .nda-document p { margin-bottom: 5pt; }
    .nda-accepted-block { display: block !important; page-break-after: avoid; }
    .nda-page-wrapper { background: #fff !important; }
    .nda-disclosure { page-break-inside: avoid; border: 1pt solid #E5E7EB !important; border-radius: 0 !important; margin: 14pt 0 8pt !important; }
    .nda-disclosure-header { background: #F9FAFB !important; padding: 5pt 12pt !important; border-bottom: 1pt solid #E5E7EB !important; }
    .nda-disclosure-body { padding: 10pt 12pt !important; max-height: none !important; overflow: visible !important; font-size: 9.5pt; line-height: 1.55; color: #000 !important; }
  }
  @media screen { .nda-accepted-block { display: none; } }
`;

export interface NDADocumentProps {
  acceptedBy?:   string;
  acceptedAt?:   string;
  email?:        string;
  phone?:        string;
  isBusiness?:   boolean;
  businessName?: string;
  ipAddress?:    string | null;
  signature?:    string | null;
  adminView?:    boolean;
}

// ─── Disclosure Statement ─────────────────────────────────────────────────────

export function NDADisclosure() {
  return (
    <div className="nda-disclosure bg-white border border-[#E5E7EB] rounded-lg shadow-sm overflow-hidden">
      <div className="nda-disclosure-header px-6 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-[#7B1A1A]">Disclosure Statement</span>
      </div>
      <div className="nda-disclosure-body px-6 py-5 overflow-y-auto text-sm text-[#364153] leading-relaxed space-y-3" style={{ maxHeight: "180px" }}>
        <p>
          By electronically accepting this Agreement you confirm that your acceptance constitutes a valid and
          legally binding act in accordance with the{" "}
          <strong>Electronic Communications and Transactions Act 25 of 2002 (ECTA)</strong>.
        </p>
        <p>
          LEO Group will retain a permanent electronic record of this acceptance — including your full name,
          email address, IP address, acceptance timestamp, NDA version, and optional handwritten signature —
          for audit, evidentiary, and enforcement purposes.
        </p>
        <p>
          This record carries the same legal force and effect as a wet-ink signature and may be relied upon in
          any competent court or regulatory proceeding. You confirm that you have the legal capacity and authority
          to enter into this Agreement, whether in a personal or representative capacity.
        </p>
      </div>
    </div>
  );
}

// ─── NDA Document ─────────────────────────────────────────────────────────────

export function NDADocument({
  acceptedBy,
  acceptedAt,
  email,
  phone,
  isBusiness,
  businessName,
  ipAddress,
  signature,
  adminView = false,
}: NDADocumentProps) {
  return (
    <>
      <style>{NDA_PRINT_STYLES}</style>
      <div className="nda-document text-sm text-[#101828] leading-relaxed space-y-5">

        {/* Signature / Acceptance block — TOP of document */}
        {acceptedBy && (
          <div className={adminView ? "pb-6 border-b-2 border-[#101828]" : "nda-accepted-block pb-6 border-b-2 border-[#101828]"}>
            <p className="text-xs font-semibold text-[#101828] uppercase tracking-wide mb-3">Electronic Acceptance Record</p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs text-[#364153]">
              <div><span className="font-semibold">Accepted by:</span> {acceptedBy}</div>
              <div><span className="font-semibold">Date &amp; Time:</span> {acceptedAt ?? "—"}</div>
              {email     && <div><span className="font-semibold">Email:</span> {email}</div>}
              {phone     && <div><span className="font-semibold">Phone:</span> {phone}</div>}
              {ipAddress && <div><span className="font-semibold">IP Address:</span> {ipAddress}</div>}
              {isBusiness && businessName && (
                <div className="col-span-2"><span className="font-semibold">Business:</span> {businessName}</div>
              )}
              <div><span className="font-semibold">NDA Version:</span> NDA 2026</div>
              <div><span className="font-semibold">Method:</span> Electronic click-through (ECTA)</div>
            </div>
            {signature && (
              <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
                <p className="text-xs font-semibold text-[#101828] mb-2">Recipient Signature</p>
                <img
                  src={signature}
                  alt="Recipient signature"
                  className="border border-[#E5E7EB] rounded bg-white"
                  style={{ maxWidth: "280px", height: "80px", objectFit: "contain" }}
                />
                <div
                  className="mt-1 text-xs text-[#4A5565]"
                  style={{ maxWidth: "280px", borderTop: "1px solid #101828", paddingTop: "4px" }}
                >
                  {acceptedBy} · {acceptedAt}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Header */}
        <div className="text-center border-b border-[#E5E7EB] pb-6">
          <p className="text-xs uppercase tracking-widest text-[#7B1A1A] font-semibold mb-1">Version: NDA 2026</p>
          <h2 className="text-xl font-semibold tracking-tight">NON-DISCLOSURE AGREEMENT</h2>
          <p className="text-xs text-[#4A5565] mt-2 font-medium">LEO Group Holding Company (Pty) Ltd</p>
          <p className="text-xs text-[#4A5565]">Waterford Court Office Park, 236 Glover Ave, Die Hoewes, Centurion, 0163</p>
        </div>

        {/* Recipient details — shown when acceptedBy is set */}
        {acceptedBy && (
          <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-md p-4 text-xs text-[#364153]">
            <p className="text-[10px] uppercase tracking-widest text-[#9CA3AF] font-semibold mb-2">Recipient Details</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
              <div><span className="font-semibold text-[#101828]">Name: </span>{acceptedBy}</div>
              {email        && <div><span className="font-semibold text-[#101828]">Email: </span>{email}</div>}
              {phone        && <div><span className="font-semibold text-[#101828]">Phone: </span>{phone}</div>}
              {ipAddress    && <div><span className="font-semibold text-[#101828]">IP Address: </span>{ipAddress}</div>}
              {isBusiness && businessName && (
                <div className="col-span-2"><span className="font-semibold text-[#101828]">Business: </span>{businessName}</div>
              )}
              <div><span className="font-semibold text-[#101828]">Type: </span>{isBusiness ? "Business Representative" : "Individual"}</div>
            </div>
          </div>
        )}

        <section>
          <h3 className="font-semibold text-[#101828] mb-2">1. PARTIES</h3>
          <p className="text-[#364153]">
            This Non-Disclosure Agreement (<strong>"Agreement"</strong>) is entered into between{" "}
            <strong>LEO Group Holding Company (Pty) Ltd</strong> (<strong>"LEO Group"</strong>, the{" "}
            <strong>"Disclosing Party"</strong>), a private company duly incorporated in the Republic of South Africa,
            and the individual whose name, email address, IP address, and acceptance timestamp are electronically
            recorded at the time of acceptance (<strong>"Recipient"</strong>).
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-[#101828] mb-2">2. DEFINITIONS</h3>
          <p className="text-[#364153] mb-2">
            <strong>"Confidential Information"</strong> means any and all information disclosed by LEO Group to the
            Recipient, whether in writing, orally, visually, electronically, or by any other means, including but not
            limited to: business plans, financial data, pricing, client and partner details, technical specifications,
            software, platform designs, source code, trade secrets, strategies, processes, and any other information
            that is designated as confidential or that a reasonable person would recognise as confidential given its
            nature or the circumstances of disclosure.
          </p>
          <p className="text-[#364153]">
            <strong>"Permitted Purpose"</strong> means the sole purpose of evaluating a potential authorised business
            relationship with LEO Group through its secure digital access portal. Any other use is expressly prohibited.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-[#101828] mb-2">3. CONFIDENTIALITY OBLIGATIONS</h3>
          <p className="text-[#364153] mb-2">The Recipient undertakes and agrees to:</p>
          <ol className="list-[lower-alpha] pl-5 space-y-1.5 text-[#364153]">
            <li>keep all Confidential Information strictly confidential and protect it with no less than reasonable care, and in any event no less than the same degree of care it applies to its own confidential information;</li>
            <li>not disclose, share, publish, communicate, or make available any Confidential Information to any third party without the prior express written consent of LEO Group;</li>
            <li>use Confidential Information solely and exclusively for the Permitted Purpose;</li>
            <li>not use any Confidential Information to the detriment or disadvantage of LEO Group, or for any competitive or commercial purpose;</li>
            <li>promptly notify LEO Group in writing upon becoming aware of any unauthorised disclosure or use of Confidential Information.</li>
          </ol>
        </section>

        <section>
          <h3 className="font-semibold text-[#101828] mb-2">4. EXCLUSIONS</h3>
          <p className="text-[#364153] mb-2">
            The obligations under Clause 3 shall not apply to information that the Recipient can demonstrate by
            contemporaneous written evidence:
          </p>
          <ol className="list-[lower-alpha] pl-5 space-y-1.5 text-[#364153]">
            <li>is or becomes generally available to the public through no breach of this Agreement by the Recipient;</li>
            <li>was already rightfully known to the Recipient prior to disclosure by LEO Group, free of any obligation of confidentiality;</li>
            <li>is independently developed by the Recipient without any reference to or use of the Confidential Information; or</li>
            <li>is required to be disclosed by applicable law, regulation, or order of a competent court or regulatory authority — provided that the Recipient gives LEO Group prompt prior written notice, cooperates fully to seek a protective order, and discloses only the minimum information legally required.</li>
          </ol>
        </section>

        <section>
          <h3 className="font-semibold text-[#101828] mb-2">5. PROHIBITION ON DISTRIBUTION AND REPRODUCTION</h3>
          <p className="text-[#364153]">
            The Recipient shall not, without the prior express written consent of LEO Group, copy, reproduce, publish,
            distribute, transmit, upload, screen-capture, record, or otherwise reproduce or disseminate any
            Confidential Information in any form or medium, whether physical, electronic, or digital. Any unauthorised
            copying or distribution constitutes a material breach of this Agreement entitling LEO Group to immediate
            remedies.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-[#101828] mb-2">6. NON-CIRCUMVENTION</h3>
          <p className="text-[#364153] mb-2">
            The Recipient agrees not to circumvent, avoid, bypass, or undermine LEO Group in any transaction,
            business opportunity, investment, or commercial relationship introduced or connected to the Confidential
            Information.
          </p>
          <p className="text-[#364153]">
            Without prior written consent, the Recipient shall not approach, contact, solicit, or engage — directly
            or indirectly — any client, investor, partner, supplier, contractor, or associate of LEO Group identified
            through, or in connection with, the Confidential Information or this Agreement.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-[#101828] mb-2">7. NON-SOLICITATION</h3>
          <p className="text-[#364153]">
            During the term of this Agreement and for a period of <strong>two (2) years</strong> from the date of
            termination or expiry, the Recipient shall not, directly or indirectly: (a) solicit, recruit, headhunt,
            or employ any current or former employee, contractor, consultant, or agent of LEO Group; or (b) induce,
            encourage, or procure any such person to terminate, reduce, or alter their engagement with LEO Group.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-[#101828] mb-2">8. INTELLECTUAL PROPERTY</h3>
          <p className="text-[#364153] mb-2">
            All Confidential Information, and any materials, outputs, or works derived therefrom, remain the sole and
            exclusive intellectual property of LEO Group. Nothing in this Agreement transfers, assigns, or grants the
            Recipient any licence, right, title, or interest in any intellectual property owned or licensed by LEO Group.
          </p>
          <p className="text-[#364153]">
            The Recipient acknowledges that LEO Group's intellectual property has substantial commercial value and that
            any unauthorised use, reproduction, or disclosure would cause irreparable harm that cannot be adequately
            remedied by an award of damages alone.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-[#101828] mb-2">9. TERM AND TERMINATION</h3>
          <p className="text-[#364153] mb-2">
            This Agreement commences on the date of electronic acceptance and remains binding for a period of{" "}
            <strong>five (5) years</strong>, unless terminated earlier by LEO Group by written notice.
          </p>
          <p className="text-[#364153]">
            Upon expiry or termination, the Recipient shall promptly and permanently destroy or return all Confidential
            Information in any form, and certify compliance in writing if requested. Clauses 3, 5, 6, 7, 8, 10, and 11
            survive termination or expiry and remain in full force and effect.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-[#101828] mb-2">10. REMEDIES FOR BREACH</h3>
          <p className="text-[#364153] mb-2">
            The Recipient acknowledges that any breach or threatened breach of this Agreement would cause LEO Group
            serious and irreparable harm for which monetary compensation would be an inadequate remedy.
          </p>
          <p className="text-[#364153] mb-2">
            Accordingly, LEO Group shall be entitled, without prejudice to any other remedy at law or in equity, to
            seek urgent interdict, injunctive relief, or specific performance from any competent court, without the
            requirement to prove actual loss, post security, or exhaust other remedies first.
          </p>
          <p className="text-[#364153]">
            In the event of a proven breach, the Recipient shall be liable for all direct, indirect, and consequential
            damages suffered by LEO Group, including legal costs on an <strong>attorney and own client scale</strong>.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-[#101828] mb-2">11. GOVERNING LAW AND JURISDICTION</h3>
          <p className="text-[#364153] mb-2">
            This Agreement is governed by and shall be construed exclusively in accordance with the laws of the{" "}
            <strong>Republic of South Africa</strong>. The parties irrevocably submit to the exclusive jurisdiction of
            the High Court of South Africa (Gauteng Division, Pretoria), without excluding any other competent court.
          </p>
          <p className="text-[#364153]">
            Electronic acceptance of this Agreement constitutes a valid, legally binding signature in accordance with
            the <strong>Electronic Communications and Transactions Act 25 of 2002 (ECTA)</strong>, and has the same
            legal force and effect as a handwritten signature.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-[#101828] mb-2">12. ELECTRONIC ACCEPTANCE AND AUDIT RECORD</h3>
          <p className="text-[#364153] mb-2">By electronically accepting this Agreement, the Recipient confirms that:</p>
          <ol className="list-[lower-alpha] pl-5 space-y-1.5 text-[#364153]">
            <li>they have read, understood, and agree to be legally bound by every term and condition of this Agreement;</li>
            <li>their electronic acceptance is intended to have the same legal effect as a wet-ink signature under ECTA and constitutes a data message as defined therein;</li>
            <li>they possess the legal capacity and authority to enter into this Agreement, whether in a personal or representative capacity; and</li>
            <li>LEO Group will maintain an electronic record of this acceptance — including the Recipient's full name, email address, IP address, acceptance timestamp, and NDA version — for audit, evidentiary, and enforcement purposes.</li>
          </ol>
        </section>

        <section>
          <h3 className="font-semibold text-[#101828] mb-2">13. GENERAL PROVISIONS</h3>
          <ol className="list-[lower-alpha] pl-5 space-y-1.5 text-[#364153]">
            <li><strong>Entire Agreement:</strong> This Agreement constitutes the complete and exclusive agreement between the parties with respect to its subject matter, and supersedes all prior negotiations, representations, undertakings, and agreements, whether written or oral.</li>
            <li><strong>Severability:</strong> If any provision is held invalid, unlawful, or unenforceable by a court of competent jurisdiction, such provision shall be modified to the minimum extent necessary to make it enforceable. All remaining provisions continue in full force.</li>
            <li><strong>No Waiver:</strong> No failure or delay by LEO Group to exercise any right or remedy under this Agreement shall constitute a waiver of that right or remedy or of any other right or remedy.</li>
            <li><strong>Amendment:</strong> This Agreement may not be varied or amended except by a written instrument signed by a duly authorised representative of LEO Group.</li>
            <li><strong>Assignment:</strong> The Recipient may not assign, transfer, or delegate any rights or obligations under this Agreement without the prior written consent of LEO Group. LEO Group may assign its rights without restriction.</li>
          </ol>
        </section>

        <div className="border-t border-[#E5E7EB] pt-4 text-xs text-[#4A5565] text-center">
          <p>LEO Group Holding Company (Pty) Ltd · NDA 2026 · Governed by the laws of the Republic of South Africa</p>
          <p className="mt-1">Electronic Communications and Transactions Act 25 of 2002 (ECTA) applies</p>
        </div>

      </div>
    </>
  );
}
